import { Outlet, NavLink, useParams, Link } from 'react-router-dom'
import { ArrowLeft, Bot, Braces, CalendarClock, ChevronDown, ChevronsLeft, ChevronsRight, FolderOpen, LayoutDashboard, Link2, MessageSquareText, Play, Puzzle, Settings } from 'lucide-react'
import { UserProfileMenu } from '@/components/UserProfileMenu'
import {
  AnalyticsIcon,
  EditSquareIcon,
  TextCompareIcon,
  DeployedCodeIcon,
  LinkedServicesIcon,
  PromptsIcon,
  TestingAutomationIcon,
  WorkflowDefinitionIcon,
  WorkflowDefinitionOutlineIcon,
} from '@/components/icons'
import { Toaster } from '@/components/ui'
import { useAuth, useProject, useSidebarCollapse } from '@/hooks'
import { getErrorStatus } from '@/api/errors'
import { ProjectUnavailable } from './ProjectUnavailable'
import { Permissions } from '@/lib/permissions'
import { SidebarLogo } from '@/components/BrandLogo'
import { useState } from 'react'

interface NavItem {
  to: string
  label: string
  icon: React.ComponentType<{ className?: string }>
  activeIcon?: React.ComponentType<{ className?: string }>
  permission?: string
  end?: boolean
}

interface NavGroup {
  label: string
  items: NavItem[]
  collapsible?: boolean
}

const projectNavGroups: NavGroup[] = [
  {
    label: 'Workflow',
    items: [
      { to: 'workflows/definitions', label: 'Definitions', icon: WorkflowDefinitionOutlineIcon, activeIcon: WorkflowDefinitionIcon, permission: Permissions.Project.Workflow.View },
      { to: 'chains', label: 'Chains', icon: Link2, permission: Permissions.Project.Chain.View, end: false },
      { to: 'workflows/runs', label: 'Runs', icon: Play, permission: Permissions.Project.Workflow.View },
      { to: 'workflows/schedules', label: 'Schedules', icon: CalendarClock, permission: Permissions.Project.Schedule.View },
      { to: 'workflows/prompts', label: 'Prompts', icon: PromptsIcon, permission: Permissions.Project.Prompt.Read },
      { to: 'workflows/skills', label: 'Skills', icon: Puzzle, permission: Permissions.Project.Skill.Read },
      { to: 'workflows/agents', label: 'Agents', icon: Bot, permission: Permissions.Project.Agent.Read },
    ],
  },
  {
    label: 'Discovery',
    items: [
      { to: 'context', label: 'Context', icon: FolderOpen },
      { to: 'chat', label: 'Chats', icon: MessageSquareText },
      { to: 'intents', label: 'Intents', icon: EditSquareIcon, permission: Permissions.Project.Intent.Read },
    ],
  },
  {
    label: 'Project',
    items: [
      { to: 'overview', label: 'Overview', icon: LayoutDashboard },
      { to: 'backlog', label: 'Backlog', icon: TextCompareIcon },
      { to: 'analytics', label: 'Analytics', icon: AnalyticsIcon, permission: Permissions.Project.Analytics.Read },
    ],
  },
  {
    label: 'Testing & Integration',
    collapsible: true,
    items: [
      { to: 'testing-integration/test-plans', label: 'Test Plans', icon: DeployedCodeIcon, permission: Permissions.Project.TestPlan.Read },
      { to: 'testing-integration/automation', label: 'Automation', icon: TestingAutomationIcon, permission: Permissions.Project.TestPlan.Read },
      { to: 'testing-integration/environment-variables', label: 'Environment Variables', icon: Braces, permission: Permissions.Project.VariableGroups.Read },
    ],
  },
  {
    label: 'Settings',
    items: [
      { to: 'connectors', label: 'Connectors', icon: LinkedServicesIcon },
      { to: 'settings', label: 'Settings', icon: Settings, permission: Permissions.Project.Settings.Manage },
    ],
  },
]

function ProjectSidebar({ collapsed, onToggle, showNavigation }: { collapsed: boolean; onToggle: () => void; showNavigation: boolean }) {
  const { projectId } = useParams()
  const { hasPermission } = useAuth(projectId)

  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem('sidebar-collapsed-nav-groups')
      return stored ? new Set(JSON.parse(stored)) : new Set()
    } catch {
      return new Set()
    }
  })

  const toggleGroup = (label: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev)
      if (next.has(label)) {
        next.delete(label)
      } else {
        next.add(label)
      }
      localStorage.setItem('sidebar-collapsed-nav-groups', JSON.stringify([...next]))
      return next
    })
  }

  const visibleGroups = showNavigation
    ? projectNavGroups
        .map((group) => ({
          ...group,
          items: group.items.filter((item) =>
            !item.permission || hasPermission(item.permission, projectId)
          ),
        }))
        .filter((group) => group.items.length > 0)
    : []

  return (
    <aside className={`sticky top-0 z-20 h-screen shrink-0 ${collapsed ? 'w-16' : 'w-64'} border-r bg-panel flex flex-col transition-all duration-200`}>
      {collapsed && (
        <button
          onClick={onToggle}
          className="absolute top-[18px] left-full z-30 ml-1 flex h-7 w-7 items-center justify-center rounded-md border bg-card shadow-sm text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Expand sidebar"
        >
          <ChevronsRight className="h-3.5 w-3.5" />
        </button>
      )}
      <div className="flex h-16 items-center border-b px-4">
        <SidebarLogo collapsed={collapsed} />
        {!collapsed && (
          <button
            onClick={onToggle}
            className="ml-auto rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            aria-label="Collapse sidebar"
          >
            <ChevronsLeft className="h-4 w-4" />
          </button>
        )}
      </div>

      <nav className="flex-1 p-2 overflow-y-auto">
        <Link
          to="/projects"
          className={`flex items-center ${collapsed ? 'justify-center px-0' : 'gap-3 px-3'} rounded-md py-2 text-sm font-semibold text-foreground hover:bg-muted transition-colors mb-2`}
        >
          <ArrowLeft className="h-4 w-4 shrink-0" />
          {!collapsed && 'All Projects'}
        </Link>

        {visibleGroups.map((group) => {
          const isGroupCollapsed = !collapsed && !!group.collapsible && collapsedGroups.has(group.label)
          return (
            <div key={group.label} className={collapsed ? 'mt-2' : 'mt-4'}>
              {group.collapsible ? (
                !collapsed && (
                  <button
                    onClick={() => toggleGroup(group.label)}
                    className="flex w-full items-center justify-between px-3 mb-1 text-xs font-bold uppercase tracking-wide text-muted-foreground hover:text-foreground transition-colors"
                    aria-expanded={!isGroupCollapsed}
                  >
                    {group.label}
                    <ChevronDown className={`h-3 w-3 transition-transform duration-200 ${isGroupCollapsed ? '-rotate-90' : ''}`} />
                  </button>
                )
              ) : (
                !collapsed && (
                  <h3 className="px-3 mb-1 text-xs font-bold uppercase tracking-wide text-muted-foreground">
                    {group.label}
                  </h3>
                )
              )}
              {!isGroupCollapsed && (
                <div className="space-y-1">
                  {group.items.map((item) => (
                    <NavLink
                      key={item.label}
                      to={`/projects/${projectId}/${item.to}`}
                      end={item.end ?? !item.to.includes('/')}
                      className={({ isActive }) =>
                        `flex items-center ${collapsed ? 'justify-center px-0' : 'gap-3 pl-5 pr-3'} rounded-md py-2 text-base transition-colors ${
                          isActive
                            ? 'bg-primary text-primary-foreground font-normal'
                            : 'font-normal text-foreground hover:bg-muted'
                        }`
                      }
                    >
                      {({ isActive }) => {
                        const Icon = isActive && item.activeIcon ? item.activeIcon : item.icon
                        return (
                          <>
                            <Icon className="h-5 w-5 shrink-0" />
                            {!collapsed && item.label}
                          </>
                        )
                      }}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </nav>
      <UserProfileMenu collapsed={collapsed} />
    </aside>
  )
}

export function ProjectLayout() {
  const { collapsed, toggle } = useSidebarCollapse('project-sidebar-collapsed')
  const { projectId } = useParams()
  const { isError, error, isFetching, refetch } = useProject(projectId)

  const status = getErrorStatus(error)
  const isProjectGone = isError && (status === 403 || status === 404)

  return (
    <div className="flex min-h-screen app-min-width page-canvas">
      <ProjectSidebar collapsed={collapsed} onToggle={toggle} showNavigation={!isProjectGone} />
      <main className="flex-1 min-w-0">
        <div className="p-8 max-w-[1560px] mx-auto">
          {isError
            ? <ProjectUnavailable isGone={isProjectGone} isRetrying={isFetching} onRetry={() => void refetch()} />
            : <Outlet />}
        </div>
      </main>
      <Toaster />
    </div>
  )
}
